import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';

import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';

const LargeAlert = ({ buttons, description, items, onClose, title, utils }) => {

    const scrollContainerRef = useRef(null);
    
    const [animations, animationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 22 },
        opacity: 0,
        transform: 'scale(0.75)'
    }));
    const [contentAnimations, contentAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -125
    }));
    const [visible, setVisible] = useState(false);

    const onCloseAlert = async () => {

        // run animation to hide components
        const animate = () => new Promise(resolve => {
            animationsAPI.start({
                to: async next => {
                    await next({
                        opacity: 0,
                        transform: 'scale(0.75)'
                    });
                    resolve();
                }
            });
        });
        await animate();

        // set visibility flag and notify root that component has closed
        setVisible(false);
        if(typeof(onClose) === 'function') {
            onClose();
        }
    }

    const onItemClick = item => {
        onCloseAlert();
        item.onClick();
    }

    const getButtons = () => {
        return (
            <div 
            className={'row px-2 pb-1 pt-0 p-xl-3 m-0'}
            style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                width: '100%'
            }}>
                {buttons?.map((button, index, buttons) => {
                    return (
                        <div
                        key={index}
                        className={`col-12 col-xl-${parseInt(12 / buttons.length)} pt-${index === 0 ? 3 : 2} pb-${index === buttons.length - 1 ? 2 : 0} px-1 py-xl-0 px-xl-1`}>
                            <Button
                            {...button}
                            key={null}
                            label={button.text}
                            type={'large'}/>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getContent = () => {
        return (
            <animated.div style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                width: '100%',
                ...contentAnimations
            }}>
                <span style={{
                    ...Appearance.textStyles.heavyHeader(),
                    fontFamily: 'Zen Dots',
                    marginBottom: 16,
                    whiteSpace: 'normal'
                }}>{title.toUpperCase()}</span>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    marginBottom: Utils.isMobile() ? 24 : 36
                }}>{description}</span>
                {items.length > 0 && (
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {items.map((item, index) => {
                            let hasClickHandler = typeof(item.onClick) === 'function';
                            return (
                                <div 
                                className={hasClickHandler ? `view-entry ${window.theme}` : ''}
                                key={index}
                                onClick={hasClickHandler ? onItemClick.bind(this, item) : null}
                                style={{
                                    alignItems: 'center',
                                    borderTop: index !== 0 && Appearance.styles.bottomBorder(),
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: 16,
                                    minWidth: 0,
                                    width: '100%'
                                }}>
                                    <img
                                    className={'d-none d-xl-flex'} 
                                    src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                    style={{
                                        height: 45,
                                        marginRight: 18,
                                        objectFit: 'contain',
                                        width: 45
                                    }} />
                                    <div  style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            fontWeight: 'bold',
                                            marginBottom: 2
                                        }}>
                                            {item.title}
                                        </span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            flexGrow: 1
                                        }}>
                                            {item.description}
                                        </span>
                                    </div>
                                    {hasClickHandler && (
                                        <img
                                        src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                                        style={{
                                            height: 20,
                                            flex: 'none',
                                            marginLeft: 8,
                                            objectFit: 'contain',
                                            width: 15
                                        }} />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </animated.div>
        )
    }

    const runSetup = () => {


        // set visible flag and animate container into view
        setVisible(true);
        animationsAPI.start({
            opacity: 1,
            transform: 'scale(1)'
        });

        // animate content into view after a brief delay
        setTimeout(() => {
            contentAnimationsAPI.start({
                opacity: 1,
                top: 0
            });
        }, 250);
    }

    useEffect(() => {
        runSetup();
    }, []);

    return visible === true && (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            position: 'fixed',
            width: '100vw'
        }}>
            <animated.div style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.dim,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                position: 'fixed',
                opacity: animations.opacity,
                width: '100vw'
            }} />
            <animated.div 
            className={Utils.isMobile() === true ? 'safe-area-bottom-padding' : ''}
            style={{
                ...Appearance.styles.unstyledPanel(),
                display: 'flex',
                flexDirection: 'column',
                maxHeight: Utils.isMobile() ? 'calc(100% - 24px)' : 'calc(100% - 128px)',
                maxWidth: Utils.isMobile() ? 'calc(100vw - 24px)' : 750,
                ...animations
            }}>
                <div 
                ref={scrollContainerRef}
                style={{
                    overflowY: 'scroll',
                    padding: 24,
                    position: 'relative',
                    width: '100%'
                }}>
                    {getContent()}
                </div>
                {getButtons()}
            </animated.div>
        </div>
    )
}

export default LargeAlert;